/* eslint-disable react/no-unescaped-entities */
import { Apple, Smartphone } from "lucide-react";
import { useWaitlistForm } from "@/hooks/useWaitlistForm";

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold mb-4">{title}</div>
      <ul className="space-y-2.5">
        {links.map(([label, href]) => (
          <li key={label}>
            <a
              href={href}
              data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { email, setEmail, loading, onSubmit } = useWaitlistForm(
    "footer",
    "Subscribed. Check your inbox."
  );

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 bg-[#060606] pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 font-display font-black tracking-tight text-lg">
              <span className="relative inline-flex h-8 w-8 items-center justify-center">
                <span className="absolute inset-0 rounded-full conic-ring" />
                <span className="absolute inset-[3px] rounded-full bg-brand-bg" />
                <span className="relative font-mono text-[11px] font-bold text-white">SP</span>
              </span>
              <span>
                SmartPl<span className="text-brand-accent" style={{ fontFamily: "'Times New Roman', Georgia, serif" }}>A</span><span className="text-brand-accent" style={{ fontFamily: "'Times New Roman', Georgia, serif", letterSpacing: "0.5px", fontSize: "1.05em" }}>I</span>te
              </span>
            </div>
            <p className="mt-4 text-sm text-white/55 max-w-sm leading-relaxed font-light">
              Personal intelligence for whoever you are, whatever you're working toward.
            </p>
            <form onSubmit={onSubmit} data-testid="footer-newsletter-form" className="mt-6 flex gap-2">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-email"
                className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm placeholder:text-white/35 focus:outline-none focus:border-brand-accent/50"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="pill bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-white/90 disabled:opacity-60"
              >
                {loading ? "…" : "Notify me"}
              </button>
            </form>
            <div className="mt-6 flex flex-wrap gap-2">
              <span data-testid="footer-badge-ios" className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-[11px] text-white/65">
                <Apple className="w-3 h-3" />
                iOS · Coming soon
              </span>
              <span data-testid="footer-badge-android" className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-[11px] text-white/65">
                <Smartphone className="w-3 h-3" />
                Android · Coming soon
              </span>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <FooterCol
              title="Product"
              links={[
                ["MIQ", "#miq"],
                ["How it works", "#howitworks"],
                ["Features", "#features"],
                ["Pricing", "#pricing"],
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                ["About", "#"],
                ["Press", "#"],
                ["Careers", "#"],
                ["Contact", "#"],
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                ["Privacy", "#"],
                ["Terms", "#"],
                ["Cookies", "#"],
                ["Security", "#"],
              ]}
            />
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © 2026 SmartPlaite. Personal intelligence for whoever you are, whatever you're working toward.
          </p>
          <p className="text-xs text-white/40 font-mono">smartplaite.com</p>
        </div>
      </div>
    </footer>
  );
}
